<template>
  <div>
    <template v-if="matter_base_id">
      <Instructional
        :matter_base_id="matter_base_id"
        :highlight_activity_id="highlight_activity_id"
        :allows_crud="allows_crud"
        :can_delete="can_delete"
        :can_update="can_update"
        class="px-1"
      ></Instructional>
    </template>
    <template v-else>{{ $t("reusable.common.errorOccurred") }}</template>
  </div>
</template>

<script>
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import matterLanguageMixin from "@/mixins/matterLanguageMixin";

/**
 * E52 · El Diseño Instruccional de la Asignatura BASE.
 *
 * Es la puerta que faltaba. El diseño de nivel base ya existía —`use_matter_values`
 * decide que las piezas cuelguen de la Asignatura base y no del plan, y hoy la mayoría
 * de las Asignaturas del Perfil lo tienen encendido—, pero la única forma de abrirlo era
 * entrar por un plan cualquiera: se editaba un diseño compartido desde el contexto de un
 * Perfil de Egreso que no era el dueño de nada.
 *
 * Esta vista es a `InstructionalView` lo que `OldMatterProgramView` es a
 * `MatterProgramView`: el mismo componente, sin Perfil de Egreso. De ahí salen también
 * los permisos —son los de `OldMatterProgramView`, porque lo que se edita es la
 * Asignatura base y no un plan: no hay carrera ni Perfil contra los que comparar.
 */
export default {
  name: "InstructionalMatterView",
  mixins: [matterLanguageMixin],
  components: {
    Instructional: () => import("./Instructional"),
  },
  data() {
    return {
      matter_base_id: Number(this.$route.params.matter_id),
      // Deep link desde la tabla de evaluaciones del Programa, igual que en el nivel
      // Perfil de Egreso: la pieza que hay que abrir y resaltar al llegar.
      highlight_activity_id: this.$route.query.activity
        ? Number(this.$route.query.activity)
        : null,
    };
  },
  computed: {
    ...mapGetters({
      user: "getUser",
      matters: names.MATTERS,
    }),
    oldMatter() {
      if (!this.matter_base_id) return null;
      return this.matters.find((x) => x.id == this.matter_base_id);
    },
    // Los mismos grupos que editan el Programa de la Asignatura base. En el nivel Perfil
    // de Egreso la lista es más larga porque hay carrera y Perfil sobre los que pedir
    // permiso de objeto; acá no hay ninguno de los dos.
    can_update() {
      if (
        (this.user &&
          [
            "DIRECTIVOS ACADÉMICOS INSTITUCIONALES",
            "DIRECTIVOS ACADÉMICOS (CAMPUS)",
          ].some((group) => this.user.group_names.includes(group))) ||
        this.user.is_superuser
      )
        return true;
      return false;
    },
    can_delete() {
      if (
        (this.user &&
          [
            "DIRECTIVOS ACADÉMICOS INSTITUCIONALES",
            "DIRECTIVOS ACADÉMICOS (CAMPUS)",
          ].some((group) => this.user.group_names.includes(group))) ||
        this.user.is_superuser
      )
        return true;
      return false;
    },
    allows_crud() {
      if (
        this.oldMatter &&
        !this.oldMatter.is_closed &&
        (this.can_update || this.can_delete)
      ) {
        const has_permission = this.$hasPermissions("teaching.change_matter");
        if (has_permission) {
          this.$store.commit(names.MUTATE_VISIBILITY_CRUD, true);
          return has_permission;
        } else {
          return false;
        }
      } else {
        this.$store.commit(names.MUTATE_VISIBILITY_CRUD, false);
        return false;
      }
    },
  },
  methods: {},
  watch: {
    // El idioma lo declara la Asignatura base, que acá es la asignatura entera: no hay
    // plan del que pudiera venir otro.
    oldMatter: {
      immediate: true,
      handler(val) {
        if (!val) return;
        this.applyMatterLanguageById(val.default_language);
      },
    },
  },
  created() {
    this.$store.dispatch(names.FETCH_MATTER, this.matter_base_id);
  },
  mounted() {},
};
</script>

<style scoped></style>
